import React, { useEffect, useMemo, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Button, Stack, Text, UnstyledButton } from '@datavant/dart';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { sendSlackAlert, SlackChannelEnum } from './api/slack';
import { trpc } from '../utils/trpc';
import useUser from '../lib/useUser';
import { trackPendoEvent } from '../pendo';
import {
    buildRmPlgLeadMessage,
    getProviderExchangeCopy,
    getRmPlgNavIds,
    PEX_POPUP_DISMISSED_KEY,
    PEX_POST_WAITLIST_REMOUNT_COUNT,
    PEX_POST_WAITLIST_REMOUNTS_KEY,
    PEX_WAITLIST_JOINED_KEY,
    PROVIDER_EXCHANGE_ROI_CALCULATOR_PATH,
    RM_PLG_LD_METRIC_KEYS,
    useRmPlgPopupPaused,
    useRmPlgVariation,
} from './RequestManager/PLG/providerExchangeCopy';

interface ProviderExchangeCardProps {
    isSideNavOpen: boolean;
}

export const ProviderExchangeCard = ({ isSideNavOpen }: ProviderExchangeCardProps): JSX.Element | null => {
    const variation = useRmPlgVariation();
    const ldClient = useLDClient();
    const currentUser = useUser().user;
    const utils = trpc.useUtils();
    // Both keys are read once on mount so changes made this session (popup dismiss, waitlist join)
    // only take effect on the next page load — avoiding jarring mid-session card appearances/disappearances.
    const [dismissedOnMount] = useState(
        () => typeof window !== 'undefined' && localStorage.getItem(PEX_POPUP_DISMISSED_KEY) === 'true'
    );
    // While the popup is paused nobody can dismiss it, so treat "paused" like "dismissed". Read live
    // (not once on mount) because LaunchDarkly flags can arrive after the first render.
    const popupPaused = useRmPlgPopupPaused();
    const popupDone = dismissedOnMount || popupPaused;
    // Extra property on every tracking event (Pendo, LaunchDarkly, Datadog) to tell side-nav-only exposure
    // apart from popup-then-card.
    const trackingExtra = useMemo(() => (popupPaused ? { popup_paused: true } : {}), [popupPaused]);
    const [waitlistJoinedOnMount] = useState(
        () => typeof window !== 'undefined' && localStorage.getItem(PEX_WAITLIST_JOINED_KEY) === 'true'
    );
    const [postWaitlistRemountsOnMount] = useState(() => {
        if (typeof window === 'undefined') return 0;
        return parseInt(localStorage.getItem(PEX_POST_WAITLIST_REMOUNTS_KEY) ?? '0', 10);
    });
    // Decrement the counter only when the card is actually visible to the user.
    const hasDecrementedRef = useRef(false);
    const pendingRef = useRef(false);
    useEffect(() => {
        if (!isSideNavOpen || postWaitlistRemountsOnMount <= 0 || hasDecrementedRef.current) return;
        hasDecrementedRef.current = true;
        localStorage.setItem(PEX_POST_WAITLIST_REMOUNTS_KEY, String(postWaitlistRemountsOnMount - 1));
    }, [isSideNavOpen, postWaitlistRemountsOnMount]);
    const showPostJoinReminder = waitlistJoinedOnMount && postWaitlistRemountsOnMount > 0;
    const [loading, setLoading] = useState(false);
    // Start in confirmed state if re-showing after a waitlist join.
    const [submitted, setSubmitted] = useState(waitlistJoinedOnMount && postWaitlistRemountsOnMount > 0);
    const [collapsed, setCollapsed] = useState(false);

    // Visitor-level property so every Pendo event for this user can be segmented by popup state.
    useEffect(() => {
        if (!variation) return;
        // @ts-expect-error - window['pendo'] is untyped
        if (typeof window !== 'undefined' && typeof window['pendo'] !== 'undefined') {
            // @ts-expect-error - window['pendo'] is untyped
            window['pendo'].updateOptions({ visitor: { provider_exchange_popup_paused: popupPaused } });
        }
    }, [variation, popupPaused]);

    const impressionTracked = useRef(false);
    const ldImpressionTracked = useRef(false);
    useEffect(() => {
        if (
            !variation ||
            !isSideNavOpen ||
            (!popupDone && !showPostJoinReminder) ||
            (waitlistJoinedOnMount && !showPostJoinReminder) ||
            impressionTracked.current
        )
            return;
        impressionTracked.current = true;
        datadogRum.addAction('provider_exchange_card_shown', { variation, ...trackingExtra });
        trackPendoEvent('RM PLG Side Nav Impression', { variation, ...trackingExtra });
    }, [variation, isSideNavOpen, popupDone, waitlistJoinedOnMount, showPostJoinReminder, trackingExtra]);
    useEffect(() => {
        if (
            !variation ||
            !isSideNavOpen ||
            (!popupDone && !showPostJoinReminder) ||
            (waitlistJoinedOnMount && !showPostJoinReminder) ||
            ldImpressionTracked.current ||
            !ldClient
        )
            return;
        ldImpressionTracked.current = true;
        ldClient.track(RM_PLG_LD_METRIC_KEYS.sidenavImpression, { variation, ...trackingExtra });
    }, [
        variation,
        isSideNavOpen,
        popupDone,
        waitlistJoinedOnMount,
        showPostJoinReminder,
        ldClient,
        trackingExtra,
    ]);
    if (
        !variation ||
        !isSideNavOpen ||
        (!popupDone && !showPostJoinReminder) ||
        (waitlistJoinedOnMount && !showPostJoinReminder)
    ) {
        return null;
    }

    const copy = getProviderExchangeCopy(variation);
    const ids = getRmPlgNavIds(variation);

    const buildLeadMessage = (action: 'waitlist' | 'calculator') =>
        buildRmPlgLeadMessage({
            action,
            surface: 'side nav',
            variation,
            copyHeadline: copy.headline,
            tenantUUID: currentUser?.tenantUUID,
            popupPaused,
            fetchVolume: () => utils.requestManager.rmRouter.getTenantRequestVolume.fetch(),
            fetchSites: () => utils.tenant.getTenantSites.fetch(),
        });

    const handleJoinWaitlist = async (): Promise<void> => {
        const alreadyPending = pendingRef.current;
        // eslint-disable-next-line require-atomic-updates -- JS is single-threaded; no real race here
        pendingRef.current = true;
        if (alreadyPending) return;
        trackPendoEvent('RM PLG CTA Clicked', { variation, surface: 'side nav', ...trackingExtra });
        ldClient?.track(RM_PLG_LD_METRIC_KEYS.ctaClick, { variation, ...trackingExtra });
        ldClient?.track(RM_PLG_LD_METRIC_KEYS.ctaClickSidenav, { variation, ...trackingExtra });
        setLoading(true);
        try {
            await sendSlackAlert(
                SlackChannelEnum.PROVIDER_EXCHANGE_LEADS,
                await buildLeadMessage('waitlist')
            );
        } catch {
            // The lead alert is best-effort — still confirm to the user even if Slack posting fails.
        } finally {
            setLoading(false);
            localStorage.setItem(PEX_WAITLIST_JOINED_KEY, 'true');
            localStorage.setItem(PEX_POST_WAITLIST_REMOUNTS_KEY, String(PEX_POST_WAITLIST_REMOUNT_COUNT));
            setSubmitted(true);
        }
    };

    const handleLearnMore = (href: string): void => {
        // The link-out replaces Join waitlist as the primary CTA for variations with a
        // sidenavCta, so it's tracked as the same CTA click, labelled e.g. pe-c-rm-learn-more
        // to tell link-out clicks apart from Join waitlist clicks.
        const learnMoreVariation = `${variation}-learn-more`;
        trackPendoEvent('RM PLG CTA Clicked', {
            variation: learnMoreVariation,
            surface: 'side nav',
            ...trackingExtra,
        });
        ldClient?.track(RM_PLG_LD_METRIC_KEYS.ctaClick, { variation: learnMoreVariation, ...trackingExtra });
        ldClient?.track(RM_PLG_LD_METRIC_KEYS.ctaClickSidenav, {
            variation: learnMoreVariation,
            ...trackingExtra,
        });
        // Kept for continuity with existing Pendo reports; don't sum with CTA Clicked.
        trackPendoEvent('RM PLG Learn More Clicked', { variation, surface: 'side nav', ...trackingExtra });
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    const handleRoiCalculator = async (): Promise<void> => {
        const alreadyPending = pendingRef.current;
        // eslint-disable-next-line require-atomic-updates -- JS is single-threaded; no real race here
        pendingRef.current = true;
        if (alreadyPending) return;
        trackPendoEvent('RM PLG ROI Calculator Clicked', {
            variation,
            surface: 'side nav',
            ...trackingExtra,
        });
        ldClient?.track(RM_PLG_LD_METRIC_KEYS.roiCalculatorClick, { variation, ...trackingExtra });
        ldClient?.track(RM_PLG_LD_METRIC_KEYS.roiCalculatorClickSidenav, { variation, ...trackingExtra });
        try {
            await sendSlackAlert(
                SlackChannelEnum.PROVIDER_EXCHANGE_LEADS,
                await buildLeadMessage('calculator')
            );
        } catch {
            // The lead alert is best-effort — still let the user proceed to the calculator.
        } finally {
            // eslint-disable-next-line require-atomic-updates -- JS is single-threaded; no real race here
            pendingRef.current = false;
        }
        if (PROVIDER_EXCHANGE_ROI_CALCULATOR_PATH) {
            window.open(PROVIDER_EXCHANGE_ROI_CALCULATOR_PATH, '_blank', 'noopener,noreferrer');
        }
    };

    const ctaTextStyle = {
        color: '#FFF',
        fontSize: '11px',
        lineHeight: 'normal',
    } as const;

    return (
        <div
            style={{
                margin: '8px 12px',
                padding: '12px',
                borderRadius: '8px',
                background: 'linear-gradient(180deg, #2945F0, #1A32C8)',
                color: '#ffffff',
            }}
        >
            <UnstyledButton
                onClick={() => setCollapsed(c => !c)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    marginBottom: collapsed ? 0 : '6px',
                }}
            >
                <Text
                    fw={700}
                    style={{
                        color: 'rgba(255, 255, 255, 0.60)',
                        fontSize: '9px',
                        letterSpacing: '0.63px',
                        textTransform: 'uppercase',
                        lineHeight: 'normal',
                    }}
                >
                    Provider Exchange
                </Text>
                <Text size="sm" style={{ opacity: 0.7, lineHeight: 1 }}>
                    {collapsed ? '+' : '−'}
                </Text>
            </UnstyledButton>
            {!collapsed && (
                <>
                    <Text
                        fw={700}
                        size="xs"
                        style={{
                            color: '#FFF',
                            lineHeight: '15.6px',
                            marginBottom: '10px',
                            marginTop: '6px',
                        }}
                    >
                        {copy.headline}
                    </Text>
                    <Stack gap={8}>
                        {copy.sidenavCta ? (
                            <Button
                                id={ids.joinWaitlist}
                                onClick={() => handleLearnMore(copy.sidenavCta!.href)}
                                size="xs"
                                style={{ ...ctaTextStyle, backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                            >
                                {copy.sidenavCta.label} →
                            </Button>
                        ) : submitted ? (
                            <div
                                id="provider-exchange-card-early-access-confirmed"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontSize: '13px',
                                }}
                            >
                                <span style={{ fontSize: '15px', lineHeight: 1 }}>✓</span>
                                <span>
                                    <span style={{ fontWeight: 600 }}>You&apos;re on the list!</span>
                                    <span style={{ opacity: 0.8 }}> We&apos;ll be in touch.</span>
                                </span>
                            </div>
                        ) : (
                            <Button
                                id={ids.joinWaitlist}
                                onClick={() => void handleJoinWaitlist()}
                                loading={loading}
                                size="xs"
                                style={{ ...ctaTextStyle, backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                            >
                                {loading ? 'Requesting access…' : `${copy.joinWaitlistLabel} →`}
                            </Button>
                        )}
                        <Button
                            id={ids.roiCalculator}
                            onClick={() => void handleRoiCalculator()}
                            appearance="outline"
                            size="xs"
                            fullWidth
                            style={{
                                ...ctaTextStyle,
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                backgroundColor: 'transparent',
                                height: 'auto',
                                paddingBlock: '6px',
                            }}
                            styles={{ label: { whiteSpace: 'normal', lineHeight: 1.3 } }}
                        >
                            {copy.roiCalculatorLabel} →
                        </Button>
                    </Stack>
                </>
            )}
        </div>
    );
};
